import { ReactElement } from 'react';

import styled from 'styled-components';

import { Card } from '@components/card';
import { DownloadButton } from '@components/download-button';
import { IconButton } from '@components/icon-button';
import { IconFile } from '@components/icons/file';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconReplace } from '@components/icons/replace';
import { IconTrash } from '@components/icons/trash';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Color, Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type UploadedFileProps = {
  name: string;
  onClick?(): void;
  onRemove?(): void;
  onReplace?(): void;
  onPreview?(): void;
  path?: string;
  isDisabled?: boolean;
  icon?: ReactElement;
  iconColor?: Color;
  font?: Font;
  variant?: 'default' | 'small';
  base64File?: {
    filename: string;
    href: string;
  };
};

export const UploadedFile = ({
  name,
  onRemove,
  onReplace,
  onPreview,
  onClick,
  path,
  isDisabled,
  icon,
  iconColor,
  font = 'regular16',
  variant = 'default',
  base64File,
}: UploadedFileProps) => {
  return (
    <PreviewContainer
      onClick={onClick}
      data-clickable={Boolean(onClick)}
      spaceY={variant === 'default' ? 2 : 0}
      spaceX={variant === 'default' ? 4 : 2}
    >
      <Stack
        space={2}
        orientation="horizontal"
        justify="space-between"
        align="center"
        style={{ minHeight: `calc(${theme.base.height} - ${pxToRem(2)})` }}
      >
        <Filename>
          {icon ?? <IconFile color={iconColor ?? 'elementBlue100'} />}
          <Text font={font} data-file-value="true">
            {name}
          </Text>
        </Filename>
        {!isDisabled && (
          <Actions onClick={(e) => e.stopPropagation()}>
            {onPreview && (
              <IconButton variant="ghost-purple" icon={<IconOpenPreview />} onClick={onPreview} />
            )}
            <DownloadButton
              variant="ghost-purple"
              url={path}
              filename={name}
              base64File={base64File}
            />
            {onReplace && (
              <IconButton variant="ghost-purple" icon={<IconReplace />} onClick={onReplace} />
            )}
            {onRemove && (
              <IconButton
                variant="ghost-purple"
                icon={<IconTrash dataTestid="icon-trash" />}
                onClick={onRemove}
              />
            )}
          </Actions>
        )}
      </Stack>
    </PreviewContainer>
  );
};

const PreviewContainer = styled(Card).attrs({
  interactive: true,
})({
  overflow: 'hidden',

  '[data-clickable="true"]': {
    cursor: 'pointer',
  },

  '[data-file-value="true"]': {
    color: theme.form.value.color,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '&:focus-within': {
    '[data-file-value="true"]': {
      color: theme.form.value.colorFocus,
    },
  },
});

const Actions = styled(Stack).attrs({
  space: 0,
  orientation: 'horizontal',
  align: 'center',
})({
  margin: pxToRem(-8),
});

const Filename = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
})({
  minWidth: 0,
});
